// Non-admin report viewer page — embeds a Power BI report for a category resource
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Container, CPText } from "shared/components/base";
import CPPowerBIReport from "shared/components/shared/CPPowerBIReport/CPPowerBIReport";
import CPLoader from "shared/components/shared/CPLoader/CPLoader";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPFontAwesome from "shared/components/shared/CPFontAwesome/CPFontAwesome";
import { useCurrentUserQuery } from "data/user/user";
import { useGetResourceDetailQuery } from "data/resources/resource";
import {
  useGetPinnedReportsQuery,
  usePinReportMutation,
  useUnpinReportMutation,
} from "data/pins/pins";
import "./ReportPageStyles.scss";
import { models } from "powerbi-client";

const ReportPage: React.FC = () => {
  const { categoryId: categoryIdStr, resourceId: resourceIdStr } = useParams<{
    categoryId: string;
    resourceId: string;
  }>();
  const navigate = useNavigate();

  const categoryId = Number(categoryIdStr ?? 0);
  const resourceId = Number(resourceIdStr ?? 0);

  const { data: user } = useCurrentUserQuery();
  const {
    data: resource,
    isLoading,
    isError,
  } = useGetResourceDetailQuery(resourceId);

  const category = React.useMemo(
    () =>
      user?.categoryResources?.find((c) => c.categoryId === categoryId) ?? null,
    [user?.categoryResources, categoryId],
  );

  // Employees, admins, and site managers aren't scoped to specific agencies —
  // send the "CORP_USER" sentinel for the Power BI RLS "Agent Security" role
  // instead of their (empty/irrelevant) agency list.
  const isCorpUser = React.useMemo(
    () =>
      user?.roles?.some(
        (r) => r === "Admin" || r === "Site Manager" || r === "CorpUser",
      ) ?? false,
    [user?.roles],
  );

  const agencyCodes = React.useMemo(
    () =>
      isCorpUser ? ["CORP_USER"] : (user?.agencies?.map((a) => a.code) ?? []),
    [isCorpUser, user?.agencies],
  );
  const reportAgencyCodes = React.useMemo(
    () =>
      isCorpUser
        ? ["CORP_USER"]
        : resource?.agencyCodes?.length
          ? agencyCodes.filter((code) => resource.agencyCodes!.includes(code))
          : agencyCodes,
    [agencyCodes, isCorpUser, resource?.agencyCodes],
  );

  // Build the effective RLS role list to pass to the embed token.
  // "Agent Security" is the agency-scoping mechanism in Power BI — it is
  // always forwarded when configured on the report (it doesn't correspond to
  // an app-level role). Every other PBI role is only forwarded if the user
  // also holds that role in the app's own role system, which prevents them
  // from accessing report pages/visuals they haven't been granted.
  const effectiveRoles = React.useMemo(() => {
    const configured =
      resource?.pbiRoles
        ?.split(";")
        .map((r) => r.trim())
        .filter(Boolean) ?? [];
    const userRoles = user?.roles ?? [];
    return configured.filter(
      (r) => r === "Agent Security" || userRoles.includes(r),
    );
  }, [resource?.pbiRoles, user?.roles]);

  const userId = user?.userId ?? 0;
  const { data: pinnedReports = [] } = useGetPinnedReportsQuery(userId);
  const pinMutation = usePinReportMutation(userId);
  const unpinMutation = useUnpinReportMutation(userId);

  const isPinned = pinnedReports.some((p) => p.resourceId === resourceId);

  const handleTogglePin = () => {
    if (isPinned) {
      unpinMutation.mutate(resourceId);
    } else {
      pinMutation.mutate({ resourceId });
    }
  };

  // Loading
  if (isLoading) return <CPLoader isLoading />;

  // Error / not found
  if (isError || !resource) {
    return (
      <Container padding="2em" className="cp-report-page">
        <Box
          flexDirection="column"
          alignItems="center"
          style={{ marginTop: "4em" }}
        >
          <CPFontAwesome
            icon="fa-solid fa-triangle-exclamation"
            size={48}
            style={{ color: "var(--color-danger, #dc3545)", marginBottom: 16 }}
          />
          <CPText textStyle="subtitle">Report could not be loaded.</CPText>
          <CPButton
            fillMode="flat"
            themeColor="primary"
            style={{ marginTop: 12, fontSize: "0.9rem" }}
            onClick={() => navigate(-1)}
          >
            <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
            Go Back
          </CPButton>
        </Box>
      </Container>
    );
  }

  const hasPowerBIConfig = resource.workspaceId && resource.reportId;

  return (
    <Box
      className="cp-report-page"
      flexDirection="column"
      style={{ padding: 0 }}
    >
      {/* Breadcrumb + title bar */}
      <Box
        className="cp-report-header"
        alignItems="center"
        justifyContent="space-between"
        flexDirection="row"
      >
        <Box alignItems="center" spacing={8} flexDirection="row">
          <CPButton
            fillMode="flat"
            themeColor="base"
            size="small"
            onClick={() =>
              navigate(`/category/${category?.name.replace(/ /g, "-") ?? ""}`)
            }
          >
            <i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />
            {category?.name ?? "Back"}
          </CPButton>
          <i className="fa-solid fa-chevron-right cp-report-breadcrumb-sep" />
          <CPText className="cp-report-title">{resource.title}</CPText>
        </Box>
        <Box
          alignItems="center"
          spacing={8}
          flexDirection="row"
          className="cp-report-header-right"
        >
          <CPButton
            fillMode="flat"
            themeColor={isPinned ? "primary" : "base"}
            size="small"
            onClick={handleTogglePin}
            disabled={pinMutation.isLoading || unpinMutation.isLoading}
            className="cp-report-pin-btn"
          >
            <i className="fa-solid fa-thumbtack" style={{ marginRight: 6 }} />
            {isPinned ? "Unpin from Dashboard" : "Pin to Dashboard"}
          </CPButton>
        </Box>
      </Box>

      {/* Embed area */}
      <Box className="cp-report-embed-area">
        {hasPowerBIConfig ? (
          <CPPowerBIReport
            workspaceId={resource.workspaceId!}
            reportId={resource.reportId!}
            datasetId={resource.datasetId}
            defaultPageName={resource.defaultPageName}
            requiresEffectiveIdentity={resource.requiresEffectiveIdentity}
            roles={effectiveRoles}
            agencies={reportAgencyCodes}
            displayOption={models.DisplayOption.FitToPage}
            className="cp-report-pbi"
          />
        ) : (
          <Box
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            className="cp-report-unavailable"
          >
            <CPFontAwesome
              icon="fa-solid fa-chart-simple"
              size={48}
              style={{ color: "#bdbdbd", marginBottom: 16 }}
            />
            <CPText>This report is not yet configured for embedding.</CPText>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ReportPage;
